import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { auth } from "../../auth";
import { queryClient } from "../../../shared/infrastructure/api/queryClient";
import { PortalAPI } from "../infrastructure/portalApi";
import type { PortalLinkedRecord } from "../domain/types";
import {
  getActivePatientId,
  setActivePatientId as setActive,
  subscribeToActivePatientId,
} from "./portalContext";

interface PortalAuthState {
  isPatient: boolean;
  loading: boolean;
  firebaseUid: string | null;
}

export function usePortalAuth(): PortalAuthState {
  const [state, setState] = useState<PortalAuthState>({
    isPatient: false,
    loading: true,
    firebaseUid: null,
  });

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setState({ isPatient: false, loading: false, firebaseUid: null });
        return;
      }
      const tokenResult = await user.getIdTokenResult();
      const role = tokenResult.claims["role"] as string | undefined;
      setState({
        isPatient: role === "patient",
        loading: false,
        firebaseUid: user.uid,
      });
    });
    return unsub;
  }, []);

  return state;
}

interface PortalRecordsState {
  records: PortalLinkedRecord[];
  /** True until the listing has actually been answered — including before the
   *  hook is allowed to ask. An empty list is only meaningful once this is
   *  false AND `failedToLoad` is false. */
  loadingRecords: boolean;
  /** The listing could not be read. Says nothing about how many panels exist,
   *  so it must never be shown as "you have no panels". */
  failedToLoad: boolean;
  activePatientId: string | null;
  activeRecord: PortalLinkedRecord | null;
  /** Change panel: drops every cached portal value before the new one loads. */
  switchTo: (patientId: string) => void;
}

/**
 * The records this account can open, and which one it is acting on.
 *
 * The active record is held per tab in `portalContext`, outside React, because
 * the axios interceptor reads it synchronously. This subscribes to it so a
 * switch re-renders.
 */
export function usePortalRecords(enabled = true): PortalRecordsState {
  const [records, setRecords] = useState<PortalLinkedRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [failedToLoad, setFailedToLoad] = useState(false);

  const activePatientId = useSyncExternalStore(
    subscribeToActivePatientId,
    getActivePatientId,
    () => null,
  );

  useEffect(() => {
    // Not allowed to ask yet — the sign-in has not resolved. Stay loading:
    // answering "no records" here is indistinguishable from an account that
    // really holds none, and it sent patients with two professionals to the
    // empty-account screen before the request had gone out.
    if (!enabled) return;

    let cancelled = false;
    setLoadingRecords(true);
    setFailedToLoad(false);
    PortalAPI.getAccount()
      .then(({ data }) => {
        if (cancelled) return;
        setRecords(data.records);
        // One record needs no choosing, and an account whose choice no longer
        // matches what it holds must not keep sending a stale context.
        const current = getActivePatientId();
        const stillHeld = data.records.some((r) => r.patient_id === current);
        if (!stillHeld) {
          setActive(data.records.length === 1 ? data.records[0].patient_id : null);
        }
      })
      .catch(() => {
        // A request that fell over says nothing about how many panels exist.
        if (!cancelled) setFailedToLoad(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingRecords(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const switchTo = useCallback((patientId: string) => {
    // Removed, not invalidated: invalidating keeps the previous record's data
    // on screen while it refetches, which is the leak this exists to prevent.
    queryClient.removeQueries();
    setActive(patientId);
  }, []);

  const activeRecord =
    records.find((record) => record.patient_id === activePatientId) ?? null;

  return { records, loadingRecords, failedToLoad, activePatientId, activeRecord, switchTo };
}
